"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

// Module-level: persists across navigations, resets on full page reload
const injectedGlobalIds = new Set<number>();

function isVideoEmbedPath(pathname: string) {
    return /^\/fa(?:\/v2)?\/videos\/[^/]+\/embed\/?$/.test(pathname);
}

function injectHtml(html: string): HTMLElement[] {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const nodes = [
        ...Array.from(parsed.head.children),
        ...Array.from(parsed.body.children),
    ] as HTMLElement[];

    const injected: HTMLElement[] = [];
    nodes.forEach(node => {
        let el: HTMLElement;
        if (node.tagName === "SCRIPT") {
            el = document.createElement("script");
            Array.from(node.attributes).forEach(attr => el.setAttribute(attr.name, attr.value));
            el.textContent = node.textContent;
        } else {
            el = node.cloneNode(true) as HTMLElement;
        }
        document.head.appendChild(el);
        injected.push(el);
    });
    return injected;
}

export default function SeoSideEffects() {
    const pathname = usePathname();
    const shouldSkipSeoEffects = isVideoEmbedPath(pathname);
    const pageElementsRef = useRef<HTMLElement[]>([]);

    useEffect(() => {
        if (shouldSkipSeoEffects) return;

        // Remove only page-specific elements from previous navigation
        pageElementsRef.current.forEach(el => el.parentNode?.removeChild(el));
        pageElementsRef.current = [];

        const controller = new AbortController();

        const params = new URLSearchParams();
        params.set("path", encodeURIComponent(pathname));
        const sp = window.location.search.slice(1);
        if (sp) params.set("params", encodeURIComponent(sp));

        fetch(`/api/cache/pathname?${params.toString()}`, { signal: controller.signal })
            .then(r => r.json())
            .then(data => {
                const pathDetails = data?.data;
                if (!pathDetails) return;

                for (const mt of (pathDetails.meta_tags ?? [])) {
                    const isGlobal = mt.target_type === "all";

                    if (isGlobal) {
                        if (injectedGlobalIds.has(mt.id)) continue;
                        injectedGlobalIds.add(mt.id);
                        injectHtml(mt.tags); // global: no cleanup tracking
                    } else {
                        pageElementsRef.current.push(...injectHtml(mt.tags));
                    }
                }

                // eq_path.seo.script is always page-specific
                if (pathDetails?.eq_path?.seo?.script) {
                    pageElementsRef.current.push(...injectHtml(pathDetails.eq_path.seo.script));
                }
            })
            .catch(() => {});

        return () => {
            controller.abort();
            pageElementsRef.current.forEach(el => el.parentNode?.removeChild(el));
            pageElementsRef.current = [];
        };
    }, [pathname, shouldSkipSeoEffects]);

    return null;
}
